export const PHASES = [
  {
    id: "menstrual", name: "Menstrual", season: "Inner Winter", days: [1, 5], color: "#A9503A",
    mood: "Rest & restore", body: "Hormones are at their lowest. Energy turns inward; your body asks for warmth, iron and slowness.",
    eat: ["Lentil & beet stews", "Dark chocolate 70%+", "Bone broth or miso", "Ginger tea"],
    nutrients: ["Iron", "Magnesium", "Omega-3"],
    affirm: "I am allowed to rest. Softness is not weakness.",
  },
  {
    id: "follicular", name: "Follicular", season: "Inner Spring", days: [6, 13], color: "#7E9379",
    mood: "Spark & grow", body: "Estrogen rises and curiosity returns. A season for new plans, lighter meals and fresh beginnings.",
    eat: ["Sprouted grains", "Kimchi & sauerkraut", "Citrus & berries", "Eggs and avocado"],
    nutrients: ["Probiotics", "Vitamin E", "Zinc"],
    affirm: "I welcome what is new. I bloom at my own pace.",
  },
  {
    id: "ovulatory", name: "Ovulatory", season: "Inner Summer", days: [14, 16], color: "#C9834F",
    mood: "Peak radiance", body: "Your most magnetic days. Confidence and stamina peak; support your liver with fibre and antioxidants.",
    eat: ["Raw cruciferous salads", "Quinoa bowls", "Wild salmon", "Pomegranate & figs"],
    nutrients: ["Fibre", "Glutathione", "B vitamins"],
    affirm: "I take up space. My voice is a gift.",
  },
  {
    id: "luteal", name: "Luteal", season: "Inner Autumn", days: [17, 28], color: "#8C5A6B",
    mood: "Nourish & ground", body: "Progesterone climbs, cravings arrive. Complex carbs and steady protein keep mood and blood sugar calm.",
    eat: ["Roasted sweet potato", "Brown rice & chickpeas", "Pumpkin seeds", "Baked apples & cinnamon"],
    nutrients: ["Vitamin B6", "Magnesium", "Complex carbs"],
    affirm: "I honour my boundaries. I finish what matters.",
  },
];

export const BODY_TYPES = [
  { id: "ecto", name: "Ectomorph", tag: "Lean & fast-burning", note: "Calorie-dense, frequent, warming meals." },
  { id: "meso", name: "Mesomorph", tag: "Athletic & balanced", note: "Even macros, protein at every plate." },
  { id: "endo", name: "Endomorph", tag: "Curvy & steady-burning", note: "Fibre-first, lower glycemic, rich in greens." },
];

// Ectomorph = savory comfort favourite, Mesomorph = restaurant-style, Endomorph = quick 15-minute dish
export const MEALS = {
  menstrual: {
    ecto: "Braised grass-fed beef ragù over creamy polenta with garlicky organic greens and shaved parmesan.",
    meso: "Miso-glazed wild-caught salmon with sesame-ginger soba, charred bok choy and a chili-crisp drizzle.",
    endo: "15-minute coconut red lentil curry with organic spinach, ginger and warm garlic naan.",
  },
  follicular: {
    ecto: "Crispy pasture-raised chicken shawarma bowl with saffron rice, whipped garlic toum, cucumber and pickled turnips.",
    meso: "Seared pasture-raised chicken with lemon-herb orzo, blistered tomatoes and a whipped feta drizzle.",
    endo: "10-minute kimchi fried rice with organic veggies, a jammy pasture-raised egg and toasted sesame.",
  },
  ovulatory: {
    ecto: "Garlic-butter shrimp tacos on corn tortillas with charred broccoli slaw, chipotle-lime crema and pickled onions.",
    meso: "Wild salmon poke bowl with mango, edamame, avocado, brown rice and a spicy sriracha mayo.",
    endo: "15-minute Thai basil chicken lettuce wraps with crunchy organic veggies, cashews and lime.",
  },
  luteal: {
    ecto: "Smoky turkey chili loaded with black beans and sweet potato, topped with avocado and a dollop of Greek yogurt.",
    meso: "Chickpea tikka masala in a silky tomato-cream sauce with brown basmati and warm whole-wheat naan.",
    endo: "Sheet-pan honey-garlic cod with roasted butternut squash, broccolini and a pumpkin-seed crunch.",
  },
};

// Why each plate helps her phase (worded to stay true for the faith versions too)
export const MEAL_WHY = {
  menstrual: {
    ecto: "Slow-braised protein and leafy greens help replace the iron you lose on your period, and warm, soft food is gentle on a tender belly.",
    meso: "Omega-3 fats and miso help calm cramp-causing inflammation, and dark greens add magnesium to relax tight muscles.",
    endo: "Lentils and spinach bring plant iron and fibre, and ginger is a classic for easing cramps.",
  },
  follicular: {
    ecto: "Protein and whole grains fuel your rising energy, and the pickles add gut-friendly bacteria.",
    meso: "Lean protein and zinc support the follicles growing this week, and bright, fresh flavours suit your lighter days.",
    endo: "Fermented kimchi supports your gut as estrogen rises, and the protein keeps you full and focused.",
  },
  ovulatory: {
    ecto: "Fibre-packed broccoli slaw helps your body clear excess estrogen at its peak, and zinc supports a healthy ovulation.",
    meso: "Antioxidant-rich fruit, veg and healthy fats keep your peak-week glow going.",
    endo: "Light, crunchy veg and lean protein keep you energised without feeling heavy.",
  },
  luteal: {
    ecto: "Slow carbs and beans steady blood sugar and cravings, and their magnesium helps with PMS.",
    meso: "Chickpeas and brown rice bring vitamin B6 and slow carbs that help calm pre-period mood dips and cravings.",
    endo: "Lean protein and magnesium-rich pumpkin seeds help ease bloating, improve sleep and soften PMS.",
  },
};

export const FAITHS = [
  { id: "christian", name: "Christian", lines: [
    { text: "Come to me, all you who are weary and burdened, and I will give you rest.", ref: "Matthew 11:28" },
    { text: "She is clothed with strength and dignity; she can laugh at the days to come.", ref: "Proverbs 31:25" },
    { text: "I praise you because I am fearfully and wonderfully made.", ref: "Psalm 139:14" },
  ] },
  { id: "muslim", name: "Muslim", lines: [
    { text: "Allah does not burden a soul beyond that it can bear.", ref: "Qur'an 2:286" },
    { text: "For indeed, with hardship comes ease.", ref: "Qur'an 94:5" },
    { text: "And He found you lost and guided you.", ref: "Qur'an 93:7" },
  ] },
  { id: "jewish", name: "Jewish", lines: [
    { text: "He gives strength to the weary and increases the power of the weak.", ref: "Isaiah 40:29" },
    { text: "I praise You, for I am fearfully and wonderfully made.", ref: "Psalm 139:14" },
    { text: "Be strong and of good courage.", ref: "Joshua 1:9" },
  ] },
  { id: "hindu", name: "Hindu", lines: [
    { text: "The soul is neither born, nor does it ever die.", ref: "Bhagavad Gita 2:20" },
    { text: "You have a right to your actions, but never to the fruits of your actions.", ref: "Bhagavad Gita 2:47" },
    { text: "Like a lamp in a windless place that does not flicker, so is the disciplined mind.", ref: "Bhagavad Gita 6:19" },
  ] },
  { id: "buddhist", name: "Buddhist", lines: [
    { text: "Health is the greatest gift, contentment the greatest wealth.", ref: "Dhammapada 204" },
    { text: "Better than a thousand hollow words is one word that brings peace.", ref: "Dhammapada 100" },
    { text: "Hatred is never appeased by hatred; it is appeased by love.", ref: "Dhammapada 5" },
  ] },
  { id: "spiritual", name: "Spiritual", lines: [
    { text: "My body is wise. I move with its rhythm, not against it.", ref: "Daily affirmation" },
    { text: "I honour my energy without apology.", ref: "Daily affirmation" },
    { text: "I am allowed to rest. Softness is not weakness.", ref: "Daily affirmation" },
  ] },
];

const dayOfYearUTC = (d = new Date()) =>
  Math.floor((Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) - Date.UTC(d.getUTCFullYear(), 0, 0)) / 86400000);

// Must match backend emailer.daily_line (UTC day-of-year % len)
export const dailyLine = (faithId, offset = 0) => {
  const f = FAITHS.find((x) => x.id === faithId) ?? FAITHS[FAITHS.length - 1];
  return { faith: f, line: f.lines[(dayOfYearUTC() + offset) % f.lines.length] };
};

const ELEMENT_NOTE = {
  Fire: "Channel restless fire into movement — a brisk walk will steady you.",
  Earth: "Ground yourself in routine; a warm, unhurried meal is your ritual today.",
  Air: "Your mind is busy — journal three lines before bed to clear the sky.",
  Water: "Feelings run deep today. Let a bath or quiet prayer hold them.",
};

export const ZODIAC = [
  ["Aries", "Fire", "Mar 21 – Apr 19"], ["Taurus", "Earth", "Apr 20 – May 20"],
  ["Gemini", "Air", "May 21 – Jun 20"], ["Cancer", "Water", "Jun 21 – Jul 22"],
  ["Leo", "Fire", "Jul 23 – Aug 22"], ["Virgo", "Earth", "Aug 23 – Sep 22"],
  ["Libra", "Air", "Sep 23 – Oct 22"], ["Scorpio", "Water", "Oct 23 – Nov 21"],
  ["Sagittarius", "Fire", "Nov 22 – Dec 21"], ["Capricorn", "Earth", "Dec 22 – Jan 19"],
  ["Aquarius", "Air", "Jan 20 – Feb 18"], ["Pisces", "Water", "Feb 19 – Mar 20"],
].map(([name, element, dates]) => ({ id: name.toLowerCase(), name, element, dates, note: ELEMENT_NOTE[element] }));

export const MANTRAS = [
  "Live in rhythm", "Four seasons, one body", "Nourish · Pray · Glow",
  "Spend with intention", "Grace for every phase", "Your rhythm is not a flaw",
];

export const SYMPTOMS = ["Cramps", "Bloating", "Energy", "Mood", "Sleep", "Skin", "Cravings", "Libido", "Flow", "Headache", "Focus", "Discharge"];
